import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { isEqual } from 'lodash';
import { isRetryableError } from '@koob/utils';
import { PER_PAGE_FOR_HOTELS_AVAILABILITY } from '@koob/enums';
import { getTripPrograms } from '../../../api/trip';
import { getHotelsAvailability } from '../../../api/hotels';
import { useLanguage } from '../../../hooks';
import {
  useDays,
  useSelectedDay,
  useTripFilters,
  useTripStore,
} from '@koob/trip-designer';
import {
  buildExperienceFilters,
  buildHotelFilters,
} from './composeSearchFilters';

/**
 * The experiences and hotels the list and map offer for the selected day: both
 * searches follow the trip filters, the hotel one is paged.
 */
export function useComposeSearch({ tripId } = {}) {
  const tripFilters = useTripFilters();
  const selectedDay = useSelectedDay();
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const { lang } = useLanguage();

  const [hotelPage, setHotelPage] = useState(1);
  const [hotelPerPage, setHotelPerPage] = useState(
    PER_PAGE_FOR_HOTELS_AVAILABILITY,
  );
  const [hotelTotalPages, setHotelTotalPages] = useState();

  const experienceFilters = buildExperienceFilters({
    tripFilters,
    selectedDay,
  });

  const { data: experiencesData, isLoading: areProgramsLoading } = useAsync({
    promiseFn: getTripPrograms,
    // The trip language selected in the TD wins over the UI language, like
    // every other locale-aware call on this page (KD-3450). A change of either
    // reloads through watchFn: the search runs once per change (KD-3944: an
    // extra reload on mount sent every search twice).
    filters: experienceFilters,
    locale: tripFilters?.locale ?? lang,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.filters, prevProps.filters) ||
        !isEqual(props.locale, prevProps.locale)
      );
    },
  });
  const previousExperienceId = days?.[selectedDay]?.experienceId;
  const previousExperience = previousExperienceId
    ? experiencesMap?.[previousExperienceId]
    : null;
  const previousExperienceCountryId = previousExperience?.inCity?.country?.id;
  const experiencesList = (experiencesData?.data ?? []).sort((a, b) => {
    if (
      a.inCity?.country?.id === previousExperienceCountryId &&
      b.inCity?.country?.id !== previousExperienceCountryId
    ) {
      return -1;
    }
    if (
      a.inCity?.country?.id !== previousExperienceCountryId &&
      b.inCity?.country?.id === previousExperienceCountryId
    ) {
      return 1;
    }
  });

  const hotelFilters = buildHotelFilters({
    tripFilters,
    selectedDay,
    days,
    experiencesMap,
    tripId,
  });

  const hotelRequestFilters = {
    ...hotelFilters,
    page: hotelPage,
    perPage: hotelPerPage,
  };

  const {
    data: hotelsData,
    reload: reloadHotels,
    isLoading: isHotelsLoading,
  } = useAsync({
    promiseFn: getHotelsAvailability,
    locale: tripFilters?.locale ?? lang,
    filters: hotelRequestFilters,
    watchFn: (props, prevProps) => {
      return !isEqual(
        JSON.stringify(props.filters),
        JSON.stringify(prevProps.filters),
      );
    },
    onResolve: response => {
      setHotelTotalPages(response?.data?.meta?.lastPage);
      if (response?.data?.state === 'inProgress') {
        setTimeout(() => reloadHotels(), 3000);
      }
    },
    onReject: error => {
      // A 4xx will fail identically on every replay — retrying it just spins.
      if (!isRetryableError(error)) return;
      setTimeout(() => reloadHotels(), 5000);
    },
  });
  const hotels = hotelsData?.data?.data ?? [];
  const hotelTotalCount = Number(hotelsData?.data?.meta?.total || 0);

  // Reset to page 1 when the real search filters change (page/perPage excluded,
  // so paging itself doesn't re-trigger this).
  useEffect(() => {
    setHotelPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hotelFilters)]);

  return {
    experiencesList,
    hotels,
    hotelPage,
    setHotelPage,
    hotelPerPage,
    setHotelPerPage,
    hotelTotalPages,
    hotelTotalCount,
    areProgramsLoading,
    // The internal availability endpoint is synchronous ({ data, meta }, no
    // polling `state`), so rely on react-async's own loading flag. Programs
    // reload on every filter change and must surface a loader too (KD-3423).
    isLoading: isHotelsLoading || areProgramsLoading,
  };
}
